/**
 * Shared Types for Equipment Tracking (Roof & Sidewall)
 * Types used across multiple equipment modules
 */

// ============================================
// Base Types (Const Pattern)
// ============================================

/** Equipment status values */
export const EQUIPMENT_STATUS = {
  ONLINE: "Online",
  OFFLINE: "Offline",
} as const;

export type EquipmentStatus =
  (typeof EQUIPMENT_STATUS)[keyof typeof EQUIPMENT_STATUS];

/** Location/Part status values */
export const LOCATION_STATUS = {
  INSTALLED: "Installed",
  EMPTY: "Empty",
  REPAIRED: "Repaired",
} as const;

export type LocationStatus =
  (typeof LOCATION_STATUS)[keyof typeof LOCATION_STATUS];

/** Metric status codes for color coding */
export const STATUS_CODE = {
  OK: "O",
  WARNING: "W",
  ALERT: "A",
  BLANK: "B",
} as const;

export type StatusCode = (typeof STATUS_CODE)[keyof typeof STATUS_CODE];

/**
 * Validates if a value is a valid StatusCode
 */
export const isValidStatusCode = (value: unknown): value is StatusCode => {
  return Object.values(STATUS_CODE).includes(value as StatusCode);
};

/** Part details (assigned to a location) */
export interface PartInfo {
  /** Display name for the part (e.g., "MOLD-7SQ-001"). Used as PartName in CreatePart SP. */
  PartName?: string;
  /** Part manufacturer (optional, used by EAF Roof/Sidewall) */
  Make?: string;
  /** Part model (optional, used by EAF Roof/Sidewall) */
  Model?: string;
  /** Part vendor/supplier (optional, used by EAF Roof/Sidewall) */
  Vendor?: string;
  SerialNumber: string;
  /** Warehouse/inventory number (optional, used by Caster eqp tracking) */
  WarehouseNumber?: string;
  /** Drawing/blueprint reference number (optional, used by Caster eqp tracking) */
  DrawingNumber?: string;
}

// ============================================
// Tree Row Types
// ============================================

/**
 * Row types for the tree hierarchy
 * - container: Top-level containers (EAF-Online, EAF-Offline)
 * - sparesContainer: Spares containers
 * - equipment: Equipment assemblies (Roof, Sidewall)
 * - location: Positions on equipment
 * - sparePart: Spare parts in spares containers
 */
export const TREE_ROW_TYPE = {
  CONTAINER: "container",
  SPARES_CONTAINER: "sparesContainer",
  EQUIPMENT: "equipment",
  LOCATION: "location",
  SPARE_PART: "sparePart",
} as const;

export type TreeRowType = (typeof TREE_ROW_TYPE)[keyof typeof TREE_ROW_TYPE];

/**
 * Base fields shared by all tree rows
 */
interface BaseTreeRow {
  /** Unique identifier for the row (format varies by rowType) */
  readonly id: string;
  /** Hierarchical path for tree rendering */
  readonly path: string[];
  /** Discriminator for row type */
  readonly rowType: TreeRowType;
  /** Equipment/Container ID this row belongs to */
  EquipmentId: number;
  /** Heats count (meaning varies by rowType) */
  Heats: number | null;
  /** Installation date (null for containers) */
  InstalledDate: Date | null;
}

/**
 * Container row (EAF-Online, EAF-Offline)
 */
export interface ContainerTreeRow extends BaseTreeRow {
  rowType: "container";
  ContainerId: number;
  ContainerName: string;
  Heats: 0;
  InstalledDate: null;
}

/**
 * Spares container row
 */
export interface SparesContainerTreeRow extends BaseTreeRow {
  rowType: "sparesContainer";
  ContainerId: number;
  ContainerName: string;
  Heats: 0;
  InstalledDate: null;
}

/**
 * Equipment row (assemblies)
 */
export interface EquipmentTreeRow extends BaseTreeRow {
  rowType: "equipment";
  EquipmentName: string;
  Status: EquipmentStatus;
  TotalHeats: number;
  Info: string;
  InstalledDate: null;
}

/**
 * Location row (positions on equipment)
 */
export interface LocationTreeRow extends BaseTreeRow {
  rowType: "location";
  EquipmentName: string;
  Status: EquipmentStatus;
  PositionId: number;
  PositionName: string;
  PartTypeId: number;
  /** Installed part ID (null if empty) */
  L2PartId: number | null;
  /** Installed part name */
  L2PartName: string | null;
  HasAssignedPart: boolean;
  PartInfo: PartInfo | null;
  PartStatus: LocationStatus;
  /** Heats since install */
  Heats: number | null;
  /** Heats since last repair */
  HeatsSinceRepair: number | null;
  /** Total lifetime heats */
  TotalHeats: number;
  /** Status code: O/W/A/B */
  StatusCode: StatusCode;
  Info: string | null;
  Comments: string | null;
}

/**
 * Spare part row
 */
export interface SparePartTreeRow extends BaseTreeRow {
  rowType: "sparePart";
  ContainerId: number;
  ContainerName: string;
  L2PartId: number;
  L2PartName: string | null;
  PartInfo: PartInfo;
  PartTypeId: number;
  /**
   * All PartTypeIds this spare part satisfies, parsed from the SQL IAm column.
   * Includes InheritedFrom chain + own PartTypeId (e.g., [1030, 1070] for a
   * Upper-Flange-TypeA that inherits from Upper-Flange).
   * Used by InstallPartModal to match spares against position's expected type.
   */
  TypeIdentity?: number[];
  HeatsSinceRepair: number;
  TotalHeats: number;
  StatusCode: StatusCode;
  HasAssignedPart: true;
}

/**
 * Discriminated union of all tree row types
 */
export type EquipmentTreeRowUnion =
  | ContainerTreeRow
  | SparesContainerTreeRow
  | EquipmentTreeRow
  | LocationTreeRow
  | SparePartTreeRow;

/**
 * Flattened row for ag-grid tree data
 * Structure is normalized for ag-Grid tree component consumption.
 */
export interface EquipmentTreeRowFlat {
  /** Unique identifier for the row (format: "{rowType}-{id}") */
  readonly id: string;
  /** Hierarchical path for tree rendering */
  readonly path: string[];
  /** Discriminator for row type (determines which fields are populated) */
  readonly rowType: TreeRowType;
  // Container-level fields
  ContainerId?: number;
  ContainerName?: string;

  // Equipment-level fields
  EquipmentId: number;
  EquipmentName?: string;
  Status?: EquipmentStatus;

  // Location-level fields
  PositionId?: number;
  PositionName?: string;
  PartTypeId?: number;
  /**
   * All PartTypeIds this spare part satisfies, parsed from the SQL IAm column.
   * Present on sparePart rows. Used by InstallPartModal for inheritance-aware matching.
   */
  TypeIdentity?: number[];
  L2PartId?: number | null;
  L2PartName?: string | null;
  HasAssignedPart?: boolean;
  PartInfo?: PartInfo | null;
  PartStatus?: LocationStatus;
  /** Indicates position has child sub-components (parts install on children, not parent) */
  HasChildren?: boolean;

  // Part type metadata (from catPartTypes)
  /** true = interchangeable part (Panel, Tube, etc). false = structural container */
  IsReplaceable?: boolean;
  /** true = spares container (default array). Replaces hardcoded isSpareContainer() checks */
  IsArray?: boolean;
  /** Ordering priority from catPartTypes. Higher = shown first within same parent */
  SortOrder?: number;

  // Metrics
  Heats: number | null;
  HeatsSinceRepair?: number | null;
  TotalHeats?: number;
  InitialAbsoluteValue?: number;
  StatusCode?: StatusCode;

  // Last task
  InstalledDate: Date | null;
  /** TaskId of the last task */
  LastTaskId?: number | null;
  Days?: number;

  // Other
  Info?: string | null;
  Comments?: string | null;
}

// ============================================
// Type Guards
// ============================================

/** Type guard for container rows */
export const isContainerRow = (
  row: EquipmentTreeRowFlat,
): row is ContainerTreeRow => row.rowType === "container";

/** Type guard for spares container rows */
export const isSparesContainerRow = (
  row: EquipmentTreeRowFlat,
): row is SparesContainerTreeRow => row.rowType === "sparesContainer";

/** Type guard for equipment rows */
export const isEquipmentRow = (
  row: EquipmentTreeRowFlat,
): row is EquipmentTreeRow => row.rowType === "equipment";

/** Type guard for location rows */
export const isLocationRow = (
  row: EquipmentTreeRowFlat,
): row is LocationTreeRow => row.rowType === "location";

/** Type guard for spare part rows */
export const isSparePartRow = (
  row: EquipmentTreeRowFlat,
): row is SparePartTreeRow => row.rowType === "sparePart";

// ============================================
// History Entry
// ============================================

/** Valid actions for history entries */
export const HISTORY_ACTION = {
  INSTALLED: "Installed",
  UNINSTALLED: "Uninstalled",
  REPAIRED: "Repaired",
  CREATED: "Created",
  ONLINE: "Online",
  OFFLINE: "Offline",
} as const;

export type HistoryAction =
  (typeof HISTORY_ACTION)[keyof typeof HISTORY_ACTION];

/**
 * History entry (frontend model)
 */
export interface HistoryEntry {
  HistoryId: number;
  /** Single timestamp (user-editable, replaces old dual Timestamp/TaskTimestamp) */
  Date: Date | null;
  Action: HistoryAction;
  LocationName: string | null;
  Heats: number;
  User: string | null;
  Notes: string | null;
  /** Part info combined into single field for display */
  PartInfo: string | null;
  /** PartId from the task (needed for UpsertTask corrections) */
  PartId: number | null;
  /** Whether this task is a reset/repair */
  IsReset: boolean;
  /** Whether this task moved the part to a default array (spares) */
  IsDefaultArray: boolean;
  /** Timestamp of the previous task (minDate for edit validation) */
  PreviousTask: Date | null;
}

// ============================================
// Task Notes (Comments Log)
// ============================================

/**
 * Task metadata entry (notes + operator tracking)
 */
export interface TaskMetadata {
  TaskMetadataId: number;
  TaskId: number;
  Notes: string | null;
  CreatedAt: Date;
  CreatedBy: string | null;
  OperatorNumber: number | null;
}

/**
 * Task history entry for the Correct Dates modal.
 *
 * Date fields are parsed as UTC by SharedEqpRepository.getTaskHistory
 * using `moment.utc().toDate()` — same pattern as all other EQP repositories.
 */
export interface TaskHistoryEntry {
  TaskId: number;
  Timestamp: Date;
  Action: string;
  LocationName: string | null;
  PreviousTimestamp: Date | null;
  NextTimestamp: Date | null;
  IsEditable: boolean | number;
  TotalCount: number;
  /** Whether the part's latest task is an uninstall (in default array / spares) */
  IsCurrentlyUninstalled: boolean | number;
}

// ============================================
// Spare Part Selector
// ============================================

/**
 * Spare part interface for the part selector
 * API returns: { value: "PartId-StatusCode", label: "SerialNumber", StatusCode: "O/A/W/B" }
 */
export interface SparePart {
  /** Format: "PartId-StatusCode" (e.g., "202-A") */
  value: string;
  /** Serial number or part identifier */
  label: string;
  /** O=OK, A=Alert, W=Warning, B=Blank */
  StatusCode: StatusCode;
}

/**
 * Helper to extract PartId from SparePart value
 */
export const getSparePartId = (part: SparePart): number => {
  const partIdStr = part.value.split("-")[0];
  return parseInt(partIdStr, 10);
};
